import { useMemo, useState } from "react";

import { debounce } from "lodash";

import { colorsPalettes } from "@similarweb/styles";
import { DotsLoader, SearchInput } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import CompanyAutocompleteItem from "app/pages/sales-intelligence/modules/company-autocomplete/components/CompanyAutocompleteItem/CompanyAutocompleteItem";
import NoSearchResults from "app/pages/sales-intelligence/modules/company-autocomplete/components/NoSearchResults/NoSearchResults";
import useCompanyAutocompleteQuery, {
    MAX_RESULTS,
} from "app/pages/sales-intelligence/modules/company-autocomplete/queries/useCompanyAutocompleteQuery";
import { useIsCompanyLockedByQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useIsCompanyLockedByQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";

import { StyledScrollableContent, StyledSearchContainer } from "./styles";

const CompanyBreadcrumbContent = ({
    onCompanyClick,
    onWebsiteClick,
    recentCompanies = [],
}: {
    onCompanyClick: ({ key, companyName }: { key: string; companyName: string }) => void;
    onWebsiteClick: (domain: string) => void;
    recentCompanies?: IAutoCompleteSiteItem[];
}) => {
    const [term, setTerm] = useState("");
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const { data, isLoading } = useCompanyAutocompleteQuery(term, MAX_RESULTS, isDataCreditsEnabled);
    const isCompanyLocked = useIsCompanyLockedByQuota();

    const debouncedSetTerm = useMemo(() => debounce(setTerm, 250), []);

    const cleanedTerm = term.toLowerCase().replace(/,/g, " ");

    const renderSearchResults = () => {
        if (term) {
            if (Array.isArray(data) && data.length === 0) {
                return <NoSearchResults searchTerm={term} />;
            }
            if (Array.isArray(data)) {
                return data.map((item: IAutoCompleteSiteItem) => (
                    <CompanyAutocompleteItem
                        key={item.name}
                        company={item}
                        hideLabel
                        onCompanyClick={onCompanyClick}
                        onWebsiteClick={onWebsiteClick}
                        queryWords={cleanedTerm.split(" ").filter(Boolean)}
                        isLocked={isCompanyLocked(item.isPurchased)}
                    />
                ));
            }
            return null;
        }
        if (recentCompanies?.length) {
            return (
                <>
                    <Text
                        fontVariant="captionTitle"
                        color={colorsPalettes.carbon[400]}
                        padding="5px 16px"
                        display="block"
                    >
                        {translate("common.recent.searches")}
                    </Text>
                    {recentCompanies.map((recent: IAutoCompleteSiteItem, index) =>
                        recent.companyInfo ? (
                            <CompanyAutocompleteItem
                                key={index}
                                company={recent}
                                hideLabel
                                onCompanyClick={onCompanyClick}
                                onWebsiteClick={onWebsiteClick}
                                isLocked={isCompanyLocked(recent.isPurchased)}
                            />
                        ) : null,
                    )}
                </>
            );
        }
        return null;
    };

    return (
        <>
            <StyledSearchContainer isLoading={isLoading}>
                <SearchInput
                    disableClear
                    onChange={debouncedSetTerm}
                    placeholder={translate("si.company-analysis.company.autocomplete.placeholder")}
                    isLoading={isLoading}
                    loadingComponent={<DotsLoader />}
                />
            </StyledSearchContainer>
            <StyledScrollableContent>{renderSearchResults()}</StyledScrollableContent>
        </>
    );
};

export default CompanyBreadcrumbContent;
